import axios from 'axios';
import { normalize } from 'normalizr';
import { createSlice } from '@reduxjs/toolkit';

import NotificationService from '../services/NotificationService';
import { mergeShallow, getObjectKeys } from '../helpers/DataHelpers';

import * as GroupActionTypes from '../constants/GroupActionTypes';
import * as OrganizationActionTypes from '../constants/OrganizationActionTypes';
import * as OutOfOfficeActionTypes from '../constants/OutOfOfficeActionTypes';
import * as SecureActionTypes from '../constants/SecureActionTypes';
import * as AuthActionTypes from '../constants/AuthActionTypes';
import * as ChatActionTypes from '../constants/ChatActionTypes';

import * as InboxActionTypes from '../constants/InboxActionTypes';
import * as UserActionTypes from '../constants/UserActionTypes';
import { setError } from './uiReducer';
import { pendingChannel, channel, tag } from '../actions/NormalizrSchema';

import * as AppointmentActionTypes from './appointmentReducer';
import * as SavedContentActionTypes from '../constants/SavedContentActionTypes';
import * as TagReducer from './tagReducer';

// SLICE
const channelSlice = createSlice({
  name: 'CHANNEL',
  initialState: {
    loading: false,
    availableBandwidthNumbers: [],
    bandwidthSearchLoading: false,
    channels: {},
    channelIds: [],
    pendingChannels: {},
    pendingChannelIds: [],
  },
  reducers: {
    receiveChannels: receiveChannelsData,
    receiveChannelView: receiveChannelsData,
    receiveCreateChannel: (state, action) => ({
      ...state,
      channels: {
        ...state.channels,
        [action.payload.channelId]: action.payload.channel[action.payload.channelId],
      },
      channelIds: [...new Set([...state.channelIds, action.payload.channelId])],
      loading: false,
    }),
    receiveUpdateChannel: (state, action) => ({
      ...state,
      channels: {
        ...state.channels,
        [action.payload.channelId]: action.payload.channel[action.payload.channelId],
      },
      channelIds: [...new Set([...state.channelIds, action.payload.channelId])],
      loading: false,
    }),
    removeChannel: (state, action) => {
      const channelsClone = { ...state.channels };
      delete channelsClone[action.payload.channelId];

      return {
        ...state,
        channels: channelsClone,
        channelIds: state.channelIds.filter(c => c !== action.payload.channelId),
        loading: false,
      };
    },
    requestData: state => ({
      ...state,
      loading: true,
    }),
    receiveBandwidthSearch: (state, action) => ({
      ...state,
      availableBandwidthNumbers: action.payload.availableBandwidthNumbers,
      bandwidthSearchLoading: false,
    }),
    requestSearchData: state => ({
      ...state,
      bandwidthSearchLoading: true,
    }),
    setClearedBandwidthNumbers: state => ({
      ...state,
      availableBandwidthNumbers: [],
    }),
    receiveProvisionZipwhipChannel: (state, action) => ({
      ...state,
      pendingChannels: {
        ...state.pendingChannels,
        [action.payload.pendingChannelId]: action.payload.pendingChannel[action.payload.pendingChannelId],
      },
      pendingChannelIds: [...new Set([...state.pendingChannelIds, action.payload.pendingChannelId])],
    }),
    receivePendingZipwhipChannels: (state, action) => ({
      ...state,
      pendingChannels: {
        ...state.pendingChannels,
        ...action.payload.pendingChannels,
      },
      pendingChannelIds: [...action.payload.pendingChannelIds],
      loading: false,
    }),
  },
  extraReducers: {

    [InboxActionTypes.receiveInboxThreadView]: (state, action) => ({
      ...state,
      channels: {
        ...state.channels,
        ...action.payload.channels,
      },
      channelIds: [...new Set([...state.channelIds, ...action.payload.channelIds])],
      loading: false,
    }),

    [OutOfOfficeActionTypes.receiveOutOfOffices]: receiveChannelsData,
    [OutOfOfficeActionTypes.receiveOOOView]: receiveChannelsData,

    [ChatActionTypes.receiveChatThreadUserView]: receiveChannelsData,
    [ChatActionTypes.receiveChatThreadGroupView]: receiveChannelsData,

    [OrganizationActionTypes.receiveOrganizationPreferencesView]: receiveChannelsData,

    [AuthActionTypes.setUser]: receiveChannelsData,

    [UserActionTypes.receiveMyUsers]: receiveChannelsData,
    [UserActionTypes.receiveUsers]: receiveChannelsData,
    [UserActionTypes.receiveContactListView]: receiveChannelsData,
    [UserActionTypes.receiveContactList]: receiveChannelsData,
    [UserActionTypes.receiveMembersView]: receiveChannelsData,

    [AppointmentActionTypes.receiveAppointments]: receiveChannelsData,

    [GroupActionTypes.receiveGroupView]: receiveChannelsData,
    [GroupActionTypes.receiveGroups]: receiveChannelsData,

    [SecureActionTypes.receivePatientThreadChannelView]: receiveChannelsData,
    [SecureActionTypes.receivePatientThreadAllView]: receiveChannelsData,

    [SavedContentActionTypes.receiveEventsForSavedContent]: receiveSavedContentChannelsData,
  },
});

export default channelSlice.reducer;

// ACTIONS
export const {
  receiveChannels,
  receiveChannelView,
  receiveCreateChannel,
  receiveUpdateChannel,
  removeChannel,
  requestData,
  receiveBandwidthSearch,
  requestSearchData,
  setClearedBandwidthNumbers,
  receiveProvisionZipwhipChannel,
  receivePendingZipwhipChannels,
} = channelSlice.actions;

// REDUCER HELPERS

function receiveChannelsData(state, action) {
  return {
    ...state,
    channels: {
      ...state.channels,
      ...action.payload.channels,
    },
    channelIds: [...new Set([...state.channelIds, ...action.payload.channelIds])],
    ...action.payload.pendingChannelIds && action.payload.pendingChannels && {
      pendingChannels: {
        ...state.pendingChannels,
        ...action.payload.pendingChannels,
      },
      pendingChannelIds: [...action.payload.pendingChannelIds],
    },
    loading: false,
  };
}

function receiveSavedContentChannelsData(state, action) {
  return {
    ...state,
    channels: mergeShallow(action.payload.channels || {}, state.channels),
    channelIds: [...new Set([...state.channelIds, ...action.payload.channelIds])],
    ...action.payload.pendingChannelIds && action.payload.pendingChannels && {
      pendingChannels: {
        ...state.pendingChannels,
        ...action.payload.pendingChannels,
      },
      pendingChannelIds: [...action.payload.pendingChannelIds],
    },
    loading: false,
  };
}

// THUNKS -- ASYNC ACTION CREATORS

export function checkZipwhipEligibility(payload) {
  return axios.post('/zipwhip/checkEligibility', payload)
    .then(response => response.data)
    .catch((err) => {
      console.error(err.response || err); // eslint-disable-line no-console
    });
}

export function provisionZipwhipNumber(payload) {
  return dispatch =>
    axios.post('/zipwhip/provisionNumber', payload)
      .then((response) => {
        const normalized = normalize(response.data, pendingChannel);

        dispatch(receiveProvisionZipwhipChannel(getPendingChannelPayload(normalized)));
        NotificationService('createChannel', response);
        dispatch(setError(null));

        return response;
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console
        NotificationService('createChannel', err.response);
        dispatch(setError(err.response || err));
      });
}

export function fetchPendingZipwhipChannels() {
  return dispatch =>
    axios.get('/zipwhip/pending')
      .then((response) => {
        const normalized = normalize(response.data, [pendingChannel]);

        dispatch(receivePendingZipwhipChannels(getPendingChannelsPayload(normalized)));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
}

export function activatePendingChannel(payload) {
  return dispatch =>
    axios.post('/zipwhip/activate', payload)
      .then((response) => {
        const normalized = normalize(response.data, channel);

        dispatch(receiveCreateChannel(getChannelPayload(normalized)));
        dispatch(fetchPendingZipwhipChannels());
        dispatch(setError(null));

        NotificationService('createChannel', response);

        return response.data;
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console
        dispatch(setError(err.response || err));
        NotificationService('createChannel', err.response);
        throw err;
      });
}

export function fetchBandwidthSearch(search) {
  return (dispatch) => {
    dispatch(requestSearchData());
    return axios.get(`/bandwidth/availableNumbers?areaCode=${search}`)
      .then((response) => {
        dispatch(receiveBandwidthSearch(getBandwidthPayload(response.data)));
      })
      .catch(() => {
        // TODO: figure out how to handle a failed request to bandwidth.
        dispatch(receiveBandwidthSearch(getBandwidthPayload([])));
      });
  };

  function getBandwidthPayload(data) {
    return {
      availableBandwidthNumbers: data,
    };
  }
}

export function clearBandwidthNumbers() {
  return (dispatch) => {
    dispatch(setClearedBandwidthNumbers());
  };
}

export function fetchChannels() {
  return (dispatch) => {
    dispatch(requestData());

    return axios.get('/channels')
      .then((response) => {
        const normalized = normalize(response.data, [channel]);
        dispatch(receiveChannels(getChannelsPayload(normalized)));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
  };
}

export function fetchChannelsView(userIsCcr = false) {
  return (dispatch) => {
    dispatch(requestData());

    const promises = [getChannels(), TagReducer.getTags()];
    if (userIsCcr) promises.push(getPendingZipwhipChannels());

    return axios.all(promises)
      .then(axios.spread((channelsResponse, tagsResponse, zipwhipResponse) => {
        const normalizedChannels = normalize(channelsResponse.data, [channel]);
        const normalizedTags = normalize(tagsResponse.data, [tag]);
        const normalizedZipwhip = zipwhipResponse ? normalize(zipwhipResponse.data, [pendingChannel]) : null;

        dispatch(receiveChannelView(getChannelViewPayload(normalizedChannels, normalizedTags, normalizedZipwhip)));
      }))
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
  };
}

export function createChannel(channelType, payload) {
  return dispatch =>
    axios.post(getCreateChannelURL(channelType), payload)
      .then((response) => {
        const normalized = normalize(response.data, channel);

        dispatch(receiveCreateChannel(getChannelPayload(normalized)));

        dispatch(clearBandwidthNumbers());

        dispatch(setError(null));

        NotificationService('createChannel', response);
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));

        NotificationService('createChannel', err.response);
      });
}

export function updateChannel(channelId, payload) {
  return dispatch =>
    axios.patch(`/channels/${channelId}`, payload)
      .then((response) => {
        const normalized = normalize(response.data, channel);

        dispatch(receiveUpdateChannel(getChannelPayload(normalized)));

        NotificationService('updateChannel', response);
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));

        NotificationService('updateChannel', err.response);
      });
}

export function destroyChannel(channelId) {
  return dispatch =>
    axios.delete(`/channels/${channelId}`)
      .then((response) => {
        dispatch(removeChannel({ channelId }));

        NotificationService('destroyChannel', response);
      })
      .catch((err) => {
        console.error(err.response); // eslint-disable-line no-console

        dispatch(setError(err.response || err));

        NotificationService('destroyChannel', err.response);
      });
}

// AXIOS HELPERS

export function getChannelById({ channelId, patientUserId, patientOrgId }) {
  let urlPath = `/channels/${channelId}`;

  // Used for patient auth.
  if (patientUserId && patientOrgId) {
    urlPath = `/channels/patient/${channelId}?patientUserId=${patientUserId}&patientOrgId=${patientOrgId}`;
  }

  return axios.get(urlPath)
    .catch(err => console.error(err.response || err)); // eslint-disable-line no-console
}

export function getChannels() {
  return axios.get('/channels')
    .catch(err => console.error(err.response || err)); // eslint-disable-line no-console
}

export function getThreadFromChannels(userId, groupId = 0, assigned = false, following = false, direct = false) {
  let url = `/channels/from/${userId}`;

  if (groupId) {
    url = `${url}?groupId=${groupId}`;
  } else if (following) {
    url = `${url}?following=1`;
  } else if (direct) {
    url = `${url}?direct=1`;
  } else if (assigned) {
    url = `${url}?assigned=1`;
  }

  return axios.get(url)
    .catch(err => console.error(err.response || err)); // eslint-disable-line no-console
}

export function getThreadActiveChannels(userId, groupId = 0, assigned = false, following = false, direct = false) {
  let url = `/channels/thread/${userId}`;

  if (groupId) {
    url = `${url}?groupId=${groupId}`;
  } else if (following) {
    url = `${url}?following=1`;
  } else if (direct) {
    url = `${url}?direct=1`;
  } else if (assigned) {
    url = `${url}?assigned=1`;
  }

  return axios.get(url)
    .catch((err) => {
      console.error(err.response || err); // eslint-disable-line no-console
    });
}

function getCreateChannelURL(channelType) {
  let url;
  if (channelType === 'sms') {
    url = '/channels/sms';
  } else if (channelType === 'secure') {
    url = '/channels/secure';
  } else if (channelType === 'facebook') {
    url = '/channels/facebook';
  } else if (channelType === 'landline') {
    url = '/channels/twilio';
  } else if (channelType === 'rhinogram') {
    url = '/channels/rhinogram';
  }

  return url;
}

function getPendingZipwhipChannels() {
  return axios.get('/zipwhip/pending')
    .catch(err => console.error(err.response || err)); // eslint-disable-line no-console
}

// PREPARE CALLBACKS -- PAYLOAD CUSTOMIZERS

function getPendingChannelsPayload(normalizedChannels) {
  return {
    pendingChannels: {
      ...normalizedChannels.entities.pendingChannels,
    },
    pendingChannelIds: normalizedChannels.result,
  };
}

function getPendingChannelPayload(normalizedChannel) {
  return {
    pendingChannel: {
      ...normalizedChannel.entities.pendingChannels,
    },
    pendingChannelId: normalizedChannel.result,
  };
}

export function getChannelsPayload(normalizedChannel) {
  return {
    channels: {
      ...normalizedChannel.entities.channels,
    },
    channelIds: normalizedChannel.result,
    phones: {
      ...normalizedChannel.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedChannel.entities.phones),
    tags: {
      ...normalizedChannel.entities.tags,
    },
    tagIds: getObjectKeys(normalizedChannel.entities.tags),
    emails: {
      ...normalizedChannel.entities.emails,
    },
    emailIds: getObjectKeys(normalizedChannel.entities.emails),
  };
}

function getChannelPayload(normalizedChannel) {
  return {
    channel: {
      ...normalizedChannel.entities.channels,
    },
    channelId: normalizedChannel.result,
    phones: {
      ...normalizedChannel.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedChannel.entities.phones),
    emails: {
      ...normalizedChannel.entities.emails,
    },
    emailIds: getObjectKeys(normalizedChannel.entities.emails),
  };
}

function getChannelViewPayload(normalizedChannels, normalizedTags, normalizedZipwhip) {
  return {
    ...getChannelsPayload(normalizedChannels),
    ...normalizedZipwhip && { ...getPendingChannelsPayload(normalizedZipwhip) },
    ...TagReducer.getTagsPayload(normalizedTags),
  };
}
